import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { CountryCache, GenCountryKey } from '../cache/CountryCache';
import { TagCache } from '../cache/TagCache';
import { SumNumber } from './ArrayHelper';
import { toIso2Code } from './IsoCodeHelper';
import { FilterItem } from './SmartSendFilterApplier';
import { GenerateSourceFullUrl } from './SourceUrlHelper';
import { IsSmartSend, ToItemSource } from './SmartSendSummaryGenerator';
import ProviderName from './ProviderName';
import { AccountNameCache, AccountSourceCache } from '../cache/AccountNameCache';
import { MarketplaceService } from '../consts/ItemMetaConstants';

export default function GenerateFilterOptions(items: SmartSendItemApiModel[], availableFilters: FilterItem[]) {
  // Options
  items
    .filter(IsSmartSend)
    .filter((x) => (x.Status === 'Active' || x.Status === 'Inactive') && x.MarketplaceStatus === 'Sold')
    .forEach((i) => {
      availableFilters.forEach((f) => {
        if (f.name === 'MarketplaceSource') {
          if (!f.options.find((x) => x.value === i.Source.Id)) {
            f.options.push({
              text: i.Source.Name,
              value: i.Source.Id,
              image: GenerateSourceFullUrl(i.Source)
            });
          }
          i.MarketplaceOrderLines.forEach((m) => {
            if (!f.options.find((x) => x.value === m.Source.Id)) {
              f.options.push({
                text: m.Source.Name,
                value: m.Source.Id,
                image: GenerateSourceFullUrl(m.Source)
              });
            }
          });
        }

        if (f.name === 'Country' && !f.options.find((x) => x.value === i.Recipient.CountryIsoCode)) {
          const iso2 = toIso2Code(i.Recipient.CountryIsoCode).toLocaleLowerCase();
          f.options.push({
            text: CountryCache[GenCountryKey(i.Recipient)]?.LocalisedName,
            value: i.Recipient.CountryIsoCode,
            image: `/~/.flags/svg/${iso2}.svg`
          });
        }

        if (
          f.name === 'Service' &&
          i.Service?.Id &&
          i.Service?.Name &&
          !f.options.find((x) => x.value === i.Service.Id)
        ) {
          f.options.push({
            text: i.Service.Name,
            value: i.Service.Id
          });
        }

        if (
          f.name === 'ServiceProvider' &&
          i.Service?.Provider &&
          !f.options.find((x) => x.value === i.Service.Provider)
        ) {
          f.options.push({
            text: ProviderName(i.Service.Provider),
            value: i.Service.Provider
          });
        }

        if (f.name === 'Type' && i.Type && !f.options.find((x) => x.value === i.Type)) {
          f.options.push({
            text: ToItemSource(i),
            value: i.Type
          });
        }

        if (f.name === 'SubType' && i.SubType && !f.options.find((x) => x.value === i.SubType)) {
          f.options.push({
            text: i.SubType,
            value: i.SubType
          });
        }

        if (f.name === 'Date' && !f.options.find((x) => x.value === new Date(i.Created).toDateString())) {
          f.options.push({
            text: new Date(i.Created).toDateString(),
            value: new Date(i.Created).toDateString()
          });
        }

        if (
          f.name === 'OrderQuantity' &&
          !f.options.find((x) => x.value === SumNumber(i.MarketplaceOrderLines.map((x) => x.Quantity)).toString())
        ) {
          f.options.push({
            text: SumNumber(i.MarketplaceOrderLines.map((x) => x.Quantity)).toString(),
            value: SumNumber(i.MarketplaceOrderLines.map((x) => x.Quantity)).toString()
          });
        }

        if (
          f.name === 'Weight' &&
          i.PackageDimensions?.Weight &&
          !f.options.find((x) => x.value === i.PackageDimensions?.Weight.toString())
        ) {
          f.options.push({
            text: i.PackageDimensions.Weight.toString() + 'kg',
            value: i.PackageDimensions.Weight.toString()
          });
        }

        if (f.name === 'Owner') {
          i.Owners.forEach((o) => {
            if (!f.options.find((x) => x.value === o.Id)) {
              f.options.push({
                text: o.Name,
                value: o.Id
              });
            }
          });
        }

        if (f.name === 'Sku') {
          i.MarketplaceOrderLines.forEach((o) => {
            const sku = o.Product?.Sku;
            if (sku && !f.options.find((x) => x.value === sku)) {
              f.options.push({
                text: sku,
                value: sku
              });
            }
          });
        }

        if (f.name === 'MarketplaceService') {
          i.MarketplaceOrderLines.forEach((o) => {
            const mps = o.Meta[MarketplaceService]?.Value;
            if (mps && !f.options.find((x) => x.value === mps)) {
              f.options.push({
                text: mps,
                value: mps
              });
            }
          });
        }

        if (f.name === 'Account') {
          i.MarketplaceOrderLines.forEach((o) => {
            const accountId = o.Source?.AccountId;
            if (accountId && !f.options.find((x) => x.value === accountId)) {
              f.options.push({
                text: `${AccountSourceCache[o.Source.Id]}: ${AccountNameCache[accountId]}`,
                value: accountId
              });
            }
          });
        }

        if (f.name === 'ProductName') {
          i.MarketplaceOrderLines.forEach((o) => {
            const desc = o.Product?.Description;
            if (desc && !f.options.find((x) => x.value === desc)) {
              f.options.push({
                text: desc,
                value: desc
              });
            }
          });
        }

        if (f.name === 'Extra') {
          i.Extras.forEach((o) => {
            if (!f.options.find((x) => x.value === o.Id)) {
              f.options.push({
                text: o.Name,
                value: o.Id
              });
            }
          });
        }

        if (f.name === 'Tag') {
          i.Tags.forEach((o) => {
            if (!f.options.find((x) => x.value === o)) {
              f.options.push({
                text: o,
                value: o
              });
            }
            // Cache TODO. Change to DB at some point.
            if (o && !o.startsWith('Box') && !TagCache.includes(o)) {
              TagCache.push(o);
            }
          });
        }
      });
    });

  // Sorting
  availableFilters.forEach((a) => {
    a.options.sort((a, b) => (a?.text?.toLowerCase() > b?.text?.toLowerCase() ? 1 : -1));
  });

  return availableFilters;
}
